import { Box, BoxProps } from '@chakra-ui/react';
import { ReactNode } from 'react';
import { colors } from 'styles/colors';

export type FormShellProps = Omit<BoxProps, 'children'> & {
  /** e.g. back control + title — layout-only; parent provides behavior. */
  header?: ReactNode;
  /**
   * Where `header` is placed. `aboveCard` puts it on the page background, same max-width column,
   * outside the white card (e.g. B2B registration intro).
   * @default 'insideCard'
   */
  headerPlacement?: 'insideCard' | 'aboveCard';
  children: ReactNode;
  /** e.g. {@link FormActions} — primary/secondary slots from parent. */
  footer?: ReactNode;
  /** Max width of the centered column (card + gutters). Defaults to `lg`. */
  innerMaxW?: BoxProps['maxW'];
  /** Props merged onto the inner card `Box`. */
  cardProps?: BoxProps;
};

/**
 * B2B (and similar) form **layout frame**: outer responsive padding, centered max-width **card**,
 * slots for `header`, `children`, and `footer`. No routing, no API — compose inside `PageLayout` when the page needs it.
 */
export const FormShell = ({
  header,
  headerPlacement = 'insideCard',
  children,
  footer,
  innerMaxW = 'lg',
  cardProps,
  ...outerProps
}: FormShellProps) => {
  const headerBlock =
    header != null ? (
      <Box mb={{ base: 5, lg: 6 }} w='full'>
        {header}
      </Box>
    ) : null;

  return (
    <Box w='full' px={{ base: 4, lg: 6, xl: 8 }} py={{ base: 4, lg: 8 }} {...outerProps}>
      <Box maxW={innerMaxW} mx='auto' w='full'>
        {headerPlacement === 'aboveCard' ? headerBlock : null}
        <Box
          bg='white'
          borderRadius='2xl'
          borderWidth='1px'
          borderColor={colors.b2b.cardBorder}
          boxShadow='0px 4px 24px rgba(15, 23, 42, 0.06)'
          p={{ base: 4, lg: 6 }}
          overflow='visible'
          {...cardProps}
        >
          {headerPlacement === 'insideCard' ? headerBlock : null}
          <Box w='full' minW={0}>
            {children}
          </Box>
          {footer != null ? (
            <Box
              mt={{ base: 6, lg: 8 }}
              pt={{ base: 4, lg: 5 }}
              borderTopWidth='1px'
              borderColor='dark.D100'
              w='full'
              minW={0}
            >
              {footer}
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};
